<script setup lang="ts">
import { onBeforeMount, onMounted, ref, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { capitalize } from 'lodash';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useViewRuleStore } from '@/store/ViewRuleStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useConvertTextComposable } from './useConvertTextComposable';
import { useRenderStatusComposable } from './useRenderStatusComposable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Tag from 'primevue/tag';
import FlightLog from './Flight/FlightLog.vue';
import FlightRule from './Flight/FlightRule.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PreViewRuleModal from '@/components/modal/PreViewRuleModal.vue';
import TourQuoteRule from '@/components/modal/tour/TourQuoteRule.vue';
import TourLog from './tour/TourLog.vue';

const dialogRef = inject('dialogRef') as Ref;
const rule = dialogRef?.value.data.ruleValue.rule;
const quoteID = dialogRef?.value.data.ruleValue.sk;
const quoteType = dialogRef?.value.data.quoteType;
const ruleViewData = dialogRef?.value.data.ruleValue.ruleView;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();

const isLoading = ref(false);
const currentLanguage = getCurrentLocale();
const expandedRows: any = ref([]);
const groupTypeList = ref([]);
const ruleData = ref({});
const ruleView = ref({});

const { getTagSeverity, getTagValue, renderClass, renderStatusYesNo } = useRenderStatusComposable();
const { handleReplaceValueToName } = useConvertTextComposable();
const viewRuleStore = useViewRuleStore();
const groupTypeManagement = useGroupTypeStore();

const updateGroupTypes = (option: number) => {
  rule[option].tourLog.forEach((tour: any) => {
    tour.detail.groupTypes = groupTypeList.value?.filter((group) => tour.detail.groupTypes?.includes(group.sk)).map((data) => getTextVariant(currentLanguage, data['name'], data['nameFr']));
  });
};

const openModalViewRunRule = (isFlightRule: boolean, value: any, childOptionValue: number) => {
  const dialogRef = dialog.open(PreViewRuleModal, {
    props: {
      header: `${t('my_quote.rule_view')}`,
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      ruleData: value,
      isFlightRule,
      groupTypeList,
      quoteType,
      childOptionValue
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleCheckFlightAvailable = (response: any, childOptionValue: number) => {
  openModalViewRunRule(true, response, childOptionValue);
};

const handleCheckTourAvailable = (response: any, childOptionValue: number) => {
  openModalViewRunRule(false, response, childOptionValue);
};

const handleUpdateFlightRule = (option: number, childOptionValue: number) => {
  isLoading.value = true;
  viewRuleStore
    .updateFlightTourRule(quoteID, option, childOptionValue)
    .then((res) => {
      rule[option] = res.rule[option];
      updateGroupTypes(option);
      initData(option);
      handleCheckFlightAvailable(res.rule[option], childOptionValue);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleUpdateTourRule = (optionValue: number, childOptionValue: number) => {
  isLoading.value = true;
  viewRuleStore
    .updateTourRule(quoteID, optionValue, childOptionValue)
    .then((res) => {
      rule[optionValue] = res.rule[optionValue];
      updateGroupTypes(optionValue);
      initData(optionValue);
      handleCheckTourAvailable(res.rule[optionValue], childOptionValue);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const initData = (num: number) => {
  ruleData.value = rule[num];
  ruleView.value = ruleViewData;
};

const renderGroupType = async () => {
  function applyRuleValue(ruleValue: any) {
    ruleValue.tourLog.forEach((tour: any) => {
      tour.detail.groupTypes = groupTypeList.value?.filter((group) => tour.detail.groupTypes?.includes(group.sk)).map((data) => getTextVariant(currentLanguage, data['name'], data['nameFr']));
    });
  }

  groupTypeManagement.getGroupTypeList().then((response) => {
    groupTypeList.value = response.data;
    rule.forEach((ruleValue: any) => {
      applyRuleValue(ruleValue);
    });
  });
};

onBeforeMount(() => {
  initData(0);
  renderGroupType();
});

onMounted(() => {
  if (rule.length) {
    rule.forEach((option: any) => {
      option.tourLog.forEach((tour: any, index: number) => {
        expandedRows.value[index] = tour.flightLog;
      });
    });
  }
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="view-rule-tour">
    <div v-if="rule?.length > 0">
      <TourQuoteRule :ruleData="ruleData" :ruleView="ruleView"></TourQuoteRule>
      <Tabs :value="0" @update:value="initData($event)">
        <TabList>
          <Tab v-for="(_, index) in rule" :key="index" :value="index">{{ 'OPTION ' + Number(index + 1) }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel v-for="(option, index) in rule" :key="index" :value="index">
            <div>
              <div>
                <div class="rule-form">
                  <p class="font-bold m-0 text-900 text-lg">{{ t('rule.quote_form_rule') }}</p>
                  <div class="mt-2 ml-4">
                    <ul>
                      <li class="text-lg mt-1 room_categories">
                        {{ t('rule.base_price') }} <span :class="renderClass(ruleData.quoteFormRule.basePriceAvailable)">({{ renderStatusYesNo(ruleData.quoteFormRule.basePriceAvailable) }})</span>
                      </li>
                      <li class="text-lg mt-1 room_categories">
                        {{ `${$t('rule.margin_safety')} ${option.netMarginTextDisplay ?? ''}` }} <span :class="renderClass(ruleData.quoteFormRule.netMargin ?? -1)">({{ renderStatusYesNo(ruleData.quoteFormRule.netMargin ?? -1) }})</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div v-for="(tour, tourIndex) in option.tourLog" :key="'tourIndex' + tourIndex">
                  <div class="flex lead-option mb-3">
                    <div class="mr-3">{{ t('common.tour') }} {{ tourIndex + 1 }}:</div>
                    <div class="font-normal">
                      ({{ formatDate(tour.depDate, 'yyyy-LLL-dd', true, currentLanguage) }}) {{ tour.detail[`tourName${capitalize(currentLanguage)}`] }}
                      <span v-if="tour.detail[`tourInclusion${capitalize(currentLanguage)}`]">- {{ tour.detail[`tourInclusion${capitalize(currentLanguage)}`] }}</span>
                      ( {{ tour.detail[`tourDuration${capitalize(currentLanguage)}`] }} ) <span v-if="tour.detail.tourType">-{{ handleReplaceValueToName(tour.detail.tourType, 'tourType') }}</span>
                    </div>
                  </div>

                  <TourLog :option="tour">
                    <div class="heading mb-3 flex align-items-center gap-4">
                      <p class="font-bold text-900 m-0">{{ t('my_quote.tour_rule') }}</p>
                      <Tag :severity="getTagSeverity(tour?.rStatusTour)" :value="getTagValue(tour?.rStatusTour)" rounded class="tag-rule"></Tag>
                      <Button v-if="tour?.rStatusTour === null" id="run-tour-rule" class="bg-primary" :label="t('common.buttons.run_rule')" @click="handleUpdateTourRule(index, tourIndex)" />
                    </div>
                  </TourLog>

                  <FlightLog :option="tour" v-model:expandedRows="expandedRows[tourIndex]">
                    <div class="heading mt-5 mb-3 flex align-items-center gap-4">
                      <p class="font-bold m-0 text-900">{{ t('my_quote.flight_rule') }}</p>
                      <Tag :severity="getTagSeverity(tour?.rStatusFlight)" :value="getTagValue(tour?.rStatusFlight)" rounded class="tag-rule"></Tag>
                      <Button v-if="tour?.rStatusFlight === null" id="run-flight-rule" class="bg-primary" :label="t('common.buttons.run_rule')" @click="handleUpdateFlightRule(index, tourIndex)" />
                    </div>
                  </FlightLog>

                  <FlightRule :ruleData="tour"></FlightRule>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
    <div v-else class="text-center">
      <h4 class="ml-2">{{ t('common.nodata') }}</h4>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .p-chips {
    width: 100%;
  }
  .p-chips-multiple-container {
    width: 100%;
    gap: 0.1rem;
    min-height: 42px;
  }
  .p-chips-input-token,
  .p-chips-token-icon {
    display: none;
  }
  .min-w-240 {
    min-width: 240px !important;
    width: 100%;
  }
}
</style>
